import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';

export const Calendar = ({
    recurringEvents = [],
    events = [],
    renderEventContent,
    renderLegend,
    initialView,
    showPastEvents
}) => {
    const calendarRef = useRef();

    const handleRenderLegend = () => {
        if (!renderLegend) return;
        const toolbar = document.querySelector('.fc-header-toolbar');

        if (document.getElementById('calendar-legend')) return;

        const legendDiv = document.createElement('div');
        legendDiv.id = 'calendar-legend';
        legendDiv.className = 'tw-flex tw-justify-center tw-gap-[16px] tw-mt-[8px]';

        toolbar.appendChild(legendDiv);

        createRoot(legendDiv).render(renderLegend());
    };

    const handleSetDatesSet = function({ start, end }) {
        let eventsShown = [];

        const newEvents = recurringEvents.reduce(
            (arr, event) => [
                ...arr,
                ...generateRecurringEvents({
                    startDate: moment(start),
                    endDate: moment(end),
                    event,
                    excludeDates: events
                })
            ],
            []
        );
        eventsShown = [...events, ...newEvents];

        if (!showPastEvents) {
            const now = moment()
                .startOf('day')
                .subtract(1, 'minute');
            eventsShown = eventsShown.filter(({ start }) => !moment(start).isBefore(now));
        }

        calendarRef.current.removeAllEvents();
        calendarRef.current.addEventSource({ events: eventsShown });
        handleRenderLegend();
    };

    const isSameDayOfWeek = (date, daysOfWeek) => daysOfWeek.includes(date.day());

    const getSliceTimes = (date, start, end) => [
        {
            start: date.clone().set({ hour: start.hour(), minute: start.minute(), second: start.second() }),
            end: date.clone().set({ hour: end.hour(), minute: end.minute(), second: end.second() })
        }
    ];

    const getBlockRange = block => ({
        start: moment(block.start),
        end: moment(block.end)
    });

    const sliceOverlapsBlock = (slice, blockStart, blockEnd) =>
        blockStart.isBefore(slice.end) && blockEnd.isAfter(slice.start);

    const isFullyBlocked = (slice, blockStart, blockEnd) =>
        blockStart.isSameOrBefore(slice.start) && blockEnd.isSameOrAfter(slice.end);

    const splitSlice = (slice, blockStart, blockEnd) => {
        const { start, end } = slice;
        const result = [];

        if (blockStart.isAfter(start)) {
            result.push({ start, end: blockStart });
        }

        if (blockEnd.isBefore(end)) {
            result.push({ start: blockEnd, end });
        }

        return result;
    };

    const filterBlockedSlices = (slices, excludeDates, setAllDayFalse) => {
        for (const block of excludeDates) {
            const { start: blockStart, end: blockEnd } = getBlockRange(block);
            const nextSlices = [];

            for (const slice of slices) {
                if (!sliceOverlapsBlock(slice, blockStart, blockEnd)) {
                    nextSlices.push(slice);
                    continue;
                }

                if (isFullyBlocked(slice, blockStart, blockEnd)) {
                    continue;
                }

                setAllDayFalse();
                nextSlices.push(...splitSlice(slice, blockStart, blockEnd));
            }

            slices = nextSlices;
        }

        return slices;
    };

    const generateRecurringEvents = ({ startDate, endDate, event, excludeDates = [] }) => {
        const { daysOfWeek, startTime, endTime, ...newEvent } = event;
        const events = [];
        let date = moment(startDate).startOf('day');
        const start = moment(startTime || '00:00:00', 'HH:mm:ss', true);
        const end = moment(endTime || '23:59:59', 'HH:mm:ss', true);

        while (date.isSameOrBefore(endDate, 'day')) {
            if (!isSameDayOfWeek(date, daysOfWeek)) {
                date.add(1, 'day');
                continue;
            }

            let slices = getSliceTimes(date, start, end);

            const setAllDayFalse = () => {
                newEvent.allDay = false;
            };

            slices = filterBlockedSlices(slices, excludeDates, setAllDayFalse);

            for (const { start, end } of slices) {
                if (start.isBefore(end) && end.diff(start, 'minutes') > 1) {
                    events.push({
                        ...newEvent,
                        start: start.toDate(),
                        end: end.toDate()
                    });
                }
            }

            date.add(1, 'day');
        }

        return events;
    };

    useEffect(() => {
        const calendarEl =
            document.body.clientWidth >= 768
                ? document.getElementById('calendar-component')
                : document.getElementById('mobicalender');

        if (!calendarRef.current) {
            calendarRef.current = new window.FullCalendar.Calendar(calendarEl, {
                initialView: initialView || 'dayGridMonth',
                headerToolbar: {
                    left: 'prev,next',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,threeDay,timeGridDay'
                },
                views: {
                    threeDay: {
                        type: 'timeGrid',
                        duration: { days: 3 },
                        buttonText: '3 Day'
                    }
                },
                buttonText: {
                    today: 'Today',
                    month: 'Month',
                    week: 'Week',
                    day: 'Day'
                },
                allDayText: 'All-day',
                editable: false,
                droppable: false,
                dayMaxEventRows: 3,
                moreLinkClick: 'week',
                datesSet: handleSetDatesSet,
                eventContent: renderEventContent,
                dayCellDidMount: function(info) {
                    let blockedEvents = events;
                    if (!showPastEvents) {
                        const now = moment()
                            .startOf('day')
                            .subtract(1, 'minute');
                        blockedEvents = blockedEvents.filter(({ start }) => !moment(start).isBefore(now));
                    }
                    blockedEvents = blockedEvents.map(e => moment(e.start).format('YYYY-MM-DD'));

                    const blockedDates = new Set(blockedEvents);

                    const cellDateStr = moment(info.date).format('YYYY-MM-DD');
                    if (blockedDates.has(cellDateStr)) {
                        info.el.style.backgroundColor = '#ECEFF1';
                        info.el.style.pointerEvents = 'none';
                        info.el.title = 'Blocked';
                    }
                }
            });
            calendarRef.current.render();
        }

        handleSetDatesSet({
            start: moment()
                .startOf('month')
                .subtract(2, 'week'),
            end: moment()
                .endOf('month')
                .add(2, 'week')
        });

        return () => {
            if (calendarRef.current) {
                calendarRef.current.destroy();
                calendarRef.current = null;
            }
        };
    }, [events, recurringEvents]);

    return (
        <div className='itm-dtls-calendararea'>
            <div id='calendar-component' className='fc fc-media-screen fc-direction-ltr fc-theme-standard'></div>
            <div id='mobicalender'></div>
        </div>
    );
};

Calendar.propTypes = {
    events: PropTypes.arrayOf(PropTypes.object).isRequired,
    recurringEvents: PropTypes.arrayOf(PropTypes.object),
    renderEventContent: PropTypes.func,
    renderLegend: PropTypes.func,
    initialView: PropTypes.string,
    showPastEvents: PropTypes.bool
};
